import type { SvgIconComponent } from '@mui/icons-material'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import { useTheme } from '@mui/material/styles'
import Image from 'next/image'
import type { JSX } from 'react'

import { basePath } from '@front/runtime-config'

export type IconType = 'default' | 'success' | 'error' | 'warning' | 'info'

export interface IconSquareProps {
  Icon?: SvgIconComponent
  iconSrc?: string
  type?: IconType
  size?: 'normal' | 'large'
  isLoading?: boolean
}

export const IconSquare = ({
  Icon,
  iconSrc,
  type = 'default',
  size = 'normal',
  isLoading = false,
}: IconSquareProps): JSX.Element => {
  const theme = useTheme()

  const typeStyles = {
    default: {
      backgroundColor: theme.palette.background.subtle,
      '& svg': {
        color: theme.palette.text.primary,
      },
    },
    success: {
      backgroundColor: theme.palette.tag.green.backgroundLight,
      '& svg': {
        color: theme.palette.tag.green.border,
      },
    },
    error: {
      backgroundColor: theme.palette.tag.red.backgroundLight,
      '& svg': {
        color: theme.palette.tag.red.border,
      },
    },
    warning: {
      backgroundColor: theme.palette.tag.orange.backgroundLight,
      '& svg': {
        color: theme.palette.tag.orange.border,
      },
    },
    info: {
      backgroundColor: theme.palette.tag.blue.backgroundLight,
      '& svg': {
        color: theme.palette.tag.blue.border,
      },
    },
  }

  const iconSize = size === 'large' ? 'large' : 'small'
  const loaderSize = size === 'large' ? 30 : 16

  const providedIcon = iconSrc ? (
    <Image src={`${basePath}/${iconSrc}`} width={14} height={14} alt="icon" />
  ) : (
    Icon && (
      <Icon
        fontSize={iconSize}
        sx={{
          fontSize: 16,
        }}
      />
    )
  )

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: 26,
        height: 26,
        borderRadius: 8,
        ...(size === 'large' && {
          marginLeft: 'auto',
          marginRight: 'auto',
          width: '50%',
          marginBottom: theme.spacing(2),
        }),
        ...(size === 'large' && {
          width: 50,
          height: 50,
          borderRadius: 8,
        }),
        ...typeStyles[type],
      }}
    >
      {isLoading ? <CircularProgress size={loaderSize} /> : providedIcon}
    </Box>
  )
}
